import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Menu, Layout, Badge } from 'antd'
import Icon, { MenuUnfoldOutlined, MenuFoldOutlined, PlusOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './index.module.less'
import { ReactComponent as Book } from '@/assets/icons/book.svg'
import { ReactComponent as Write } from '@/assets/icons/write.svg'
import { ReactComponent as Audit } from '@/assets/icons/audit.svg'
import { ReactComponent as Export } from '@/assets/icons/export.svg'
import { ReactComponent as User } from '@/assets/icons/user.svg'
import { ReactComponent as Role } from '@/assets/icons/role.svg'
import useDictMenu, { useDomainShowRed, path as dictPath } from '@/views/dictionary/hooks/useDictMenu'
import AddDir from '@/views/dictionary/components/addDir'
import AddVersion from '@/views/dictionary/components/addVersion'
import AddKnowledge from '@/views/dictionary/knowledge/addKnowledge'
import { useTranslation } from 'react-i18next'
import { PageType, KnowledgePageType } from '@/views/dictionary/constants'
import { permissionAuth } from '@/utils/permission'

const { Sider } = Layout

const typeMap: any = {
  DICT_BROWSE: PageType.View,
  DICT_EDIT: PageType.Edit,
  DICT_AUDIT: PageType.Audit,
  KNOWLEDGE_BROWSE:PageType.View,
  KNOWLEDGE_AUDIT:PageType.Audit
}

const PrimeMenu = () => {
  const location = useLocation()
  const addDirRef = useRef<any>(null)
  const addVersionRef = useRef<any>(null)
  const addKnowledgeRef = useRef<any>(null)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const openKeysRef = useRef<any>([])
  const {
    goto,
    standard,
    viewDicts,
    editDicts,
    auditDicts,
    viewKnowledge,
    editKnowledge,
    auditKnowledge,
    knowledgeOutermostRed,
    fetchDicts,
    onNavigate,
    reload,
    gotoKnowledgeBase
  } = useDictMenu({
    includes: [PageType.View, PageType.Edit, PageType.Audit],
    knowledgeIncludes: [KnowledgePageType.View, KnowledgePageType.Edit, KnowledgePageType.Audit],
  })
  const { showRedMap } = useDomainShowRed()
  const dictRed = showRedMap.find(item => item.moduleType === 1)?.showRed
  const knowledgeRed = showRedMap.find(item => item.moduleType === 2)?.showRed
  // const standardRed = showRedMap.find(item=>item.moduleType === 3)?.showRed
  const newAuditKnowledge = useMemo(() => {
    return [
      ...auditKnowledge.map((item) => ({
        ...item,
        children: [
          ...(item.children || []),
          {
            label: t('ikg.create'), // 新建
            key: `audit-knowledge-base-${item.key}`,
            menutype: 'create-auditKnowledge-base',
            domainId: item.dictid,// 知识域的dictid为知识库的domainId
            moduleType: 2,
            icon: <Icon component={PlusOutlined as any} />,
          },
        ]
      })),
      {
        label: t('ikg.create'), // 新建
        key: 'audit-knowledge',
        menutype: 'create-auditKnowledge-domain',
        moduleType: 2,
        icon: <Icon component={PlusOutlined as any} />,
      },
    ];
  }, [auditKnowledge, t]);

  const menuItems = [
    {
      label: t('dict.viewTitle'), // 字典浏览
      key: 'DICT_BROWSE',
      icon: <Icon component={Book} />,
      children: [...viewDicts],
    },
    {
      label: t('export.title'), // 导出记录
      key: 'EXPOET_RECORD',
      icon: <Icon component={Export} />,
      path: '/export',
    },
    {
      label: t('dict.editTitle'), // 字典编辑
      key: 'DICT_EDIT',
      icon: <Icon component={Write} />,
      children: [...editDicts],
    },
    {
      label: dictRed ? (
        <>
          {/* 字典审核 */}
          <span>{t('dict.auditTitle')}</span>
          <Badge dot style={{ position: 'relative', top: -5 }}></Badge>
        </>
      ) : (
        t('dict.auditTitle')
      ),
      key: 'DICT_AUDIT',
      icon: <Icon component={Audit} />,
      children: [
        ...auditDicts,
        {
          label: t('ikg.create'), // 新建
          key: 'audit-dir',
          menutype: 'create-dir',
          icon: <Icon component={PlusOutlined as any} />,
        },
      ],
    },
    {
      label: t('dict.knowledgeView'), // 知识浏览
      key: 'KNOWLEDGE_BROWSE',
      icon: <Icon component={Book} />,
      children: [...viewKnowledge],
    },
    // {
    //   label: t('dict.knowledgeMaintain'), // 知识维护
    //   key: 'KNOWLEDGE_EDIT',
    //   icon: <Icon component={Book} />,
    //   children: [...editKnowledge],
    // },
    {
      label: knowledgeRed ? (
        <>
          {/* 知识审核 */}
          <span>{t('dict.knowledgeAudit')}</span>
          <Badge dot style={{ position: 'relative', top: -5 }}></Badge>
        </>
      ) : (
        t('dict.knowledgeAudit')
      ),
      key: 'KNOWLEDGE_AUDIT',
      icon: <Icon component={Audit} />,
      children: [...newAuditKnowledge],
    },
    {
      label: t("ikg.standard"),
      key: 'DATA_STANDARD',
      icon: <Icon component={Book} />,
      path: '/standard',
      children: [...standard]
    },
    {
      label: t('ikg.userManage'), // 用户管理
      key: 'USER_MANAGER',
      icon: <Icon component={User} />,
      path: '/user',
    },
    {
      label: t('ikg.roleManage'), // 角色管理
      key: 'USER_ROLE_MANAGER',
      icon: <Icon component={Role} />,
      path: '/role',
    },
  ].filter((item: any) => {
    return permissionAuth(item.key)
  })

  const onOpenChange = (keys: string[]) => {
    if (
      keys.length > 1 ||
      openKeysRef.current.length > keys.length ||
      JSON.stringify(keys) === JSON.stringify(openKeysRef.current)
    ) {
      openKeysRef.current = keys
      return
    }
    keys.forEach((key: string) => {
      if (typeMap[key]) {
        fetchDicts(typeMap[key])
      }
    })
    openKeysRef.current = keys
  }

  useEffect(() => {
    if (location.pathname !== '/') return
    const firstMenu = menuItems[0]
    if (firstMenu?.path) {
      navigate(firstMenu.path)
      return
    }
    if (typeMap[firstMenu?.key]) {
      navigate(dictPath[typeMap[firstMenu.key]])
      return
    }
  }, [location.pathname])

  const onKnowledgeAddSuccess = (type: 'domain' | 'base', name, domainId) => {
    // 知识库新增成功跳转至首页
    if (type === 'base') {
      navigate(`/dict-knowledge-audit?dictName=${name}&domainId=${domainId}`)
    }
    // 重新加载菜单
    reload()
  }
  return (
    <>
      <Sider width={220} theme="light" collapsible collapsed={collapsed} collapsedWidth={80} trigger={null}>
        <Menu
          items={menuItems}
          onClick={(item: any) => {
            const { props } = item.item
            if (props.path) {
              navigate(props.path)
              return
            }
            if (props.menutype === 'dict' ||
              props.menutype === 'knowledge' ||
              props.menutype === 'standard') {
              props.menutype === 'standard' ? goto(props) : onNavigate(props)
              return
            }
            if (props.menutype === 'auditKnowledge-base') {
              // 知识库跳转
              gotoKnowledgeBase(props)
              return
            }
            if (props.menutype === 'create-dir') {
              addDirRef.current.open()
              return
            }
            if (props.menutype === 'create-version') {
              addVersionRef.current.open({
                domainId: props.domainid,
                dictId: props.dictid,
                popFromType: 1,
                moduleType: props.moduleType,
                dictType: 1
              })
            }
            if (props.menutype === 'create-catalog') {
              addVersionRef.current.open({
                dictName: props.dictname,
                dictId: props.dictid,
                popFromType: 3,
                moduleType: props.moduleType,
              })
            }
            if (props.menutype === 'create-auditKnowledge-domain') {
              addKnowledgeRef.current.open({
                type: 'domain', // 知识域
                moduleType: props.moduleType,
              })
            }
            if (props.menutype === 'create-auditKnowledge-base') {
              addKnowledgeRef.current.open({
                type: 'base', // 知识库
                moduleType: props.moduleType,
                dictType: 2,//知识审核传2
                domainId: props.domainId
              })
            }
          }}
          style={{ minHeight: 'calc(100vh - 64px)' }}
          onOpenChange={onOpenChange}
        />
        <div className={styles.trigger}>
          {collapsed ? (
            <MenuUnfoldOutlined
              onClick={() => {
                setCollapsed(!collapsed)
              }}
            />
          ) : (
            <MenuFoldOutlined
              onClick={() => {
                setCollapsed(!collapsed)
              }}
            />
          )}
        </div>
      </Sider>
      <AddDir ref={addDirRef} onSuccess={reload} />
      <AddVersion ref={addVersionRef} onSuccess={reload} />
      <AddKnowledge ref={addKnowledgeRef} onSuccess={onKnowledgeAddSuccess} />
    </>
  )
}

export default PrimeMenu
